import { ITimeSlot } from 'fitbeat.gym-core';
import { BaseUserProfile, Gym, WorkoutType } from 'fitbeat.models';
import moment from 'moment-timezone';
import React, { FunctionComponent } from 'react';
import { translate } from 'react-i18next';
import {
  ActivityIndicator,
  StyleSheet,
  Text,
  TouchableOpacity,
  View,
} from 'react-native';
import { connect } from 'react-redux';
import { TranslateProp } from '../../common-types';
import {
  Button,
  ButtonSize,
  ButtonTypes,
  Icon,
} from '../../components';
import { IAppStore } from '../../HomeWorkouts';
import { InfoAlert } from '../../payments/components/InfoAlert';
import Theme from '../../theme';
import { FormError } from '../../ui';
import { defaultScale, getSlotTime } from '../../utils';
import {
  onDemandSlotSelectBackPressAction,
  onDemandSlotSelectedAction,
  startOnDemandWorkoutPressedAction,
} from '../actions';
import {
  BookingFlowFooter,
  BookingInstructionHeader,
} from '../components';
import { currentSelectedGymSelector, onDemandSlotsSelector } from '../selectors';

interface IMapStateProps {
  onDemandSlots: ITimeSlot[] | undefined;
  bookingError: string | null;
  pendingSlot: {
    startTime: Date;
    workoutType: WorkoutType;
  } | null;
  gym: Gym | undefined;
  startOnDemandBookingLoading: boolean;
  loadingOnDemandSlots: boolean;
}

interface IMapDispatchProps {
  backPressed(): void;
  onDemandSlotSelected(startTime: Date, workoutType: WorkoutType): void
  startOnDemandWorkoutPressed(): void;
}

interface IParentProps {
  userProfile: BaseUserProfile;
  workoutSessionService: any;
  t: TranslateProp;
}

const SelectOnDemandStudioTimeSlot: FunctionComponent<IMapStateProps & IMapDispatchProps & IParentProps> = (
  {
    t,
    backPressed,
    onDemandSlots,
    onDemandSlotSelected,
    pendingSlot,
    gym,
    startOnDemandBookingLoading,
    startOnDemandWorkoutPressed,
    bookingError,
    loadingOnDemandSlots,
  },
) => {
  const isSlotSelected = (startTime: number, workoutType: WorkoutType): boolean => (
    !!(
      pendingSlot
      && pendingSlot.startTime.getTime() === new Date(startTime).getTime()
      && pendingSlot.workoutType === workoutType
    )
  );

  const slotFormattedTime = (date: number) => {
    const momentDate = moment.tz(date, gym?.timezone);
    return momentDate.format('h:mma');
  };

  const getEntries = () => {
    if (!onDemandSlots || onDemandSlots.length === 0) {
      return <InfoAlert message={t('noOnDemandSlotsMessage')} />;
    }

    return onDemandSlots.map(({ workoutType, startTime }) => {
      const selected = isSlotSelected(startTime, workoutType);
      return (
        <TouchableOpacity
          style={[
            styles.optionCard,
            selected && styles.selectedOption,
          ]}
          key={workoutType}
          onPress={() => onDemandSlotSelected(new Date(startTime), workoutType)}
        >
          <Text
            style={[
              styles.slotOptionTitle,
              selected && styles.textSelected,
            ]}
          >
            {slotFormattedTime(startTime)}
          </Text>
          <Text
            style={[
              styles.slotOptionDescription,
              selected && styles.textSelected,
            ]}
          >
            {t('subheadingOnDemandSlotOption', { duration: getSlotTime({ workoutType }) })}
          </Text>
        </TouchableOpacity>
      );
    });
  };

  const nextButtonTitle = () => {
    if (gym && pendingSlot) {
      const now = moment().tz(gym.timezone);
      const pendingSlotTime = moment.tz(pendingSlot.startTime, gym.timezone);
      const minutesDiff = pendingSlotTime.diff(now.startOf('minute'), 'minutes');
      if (minutesDiff <= 0) {
        return t('startNow');
      }
      return t('onDemandBookingStartsIn', { minutes: minutesDiff });
    }
    return t('next');
  };

  return (
    <View style={styles.container}>
      <TouchableOpacity onPress={backPressed}>
        <Icon
          size={defaultScale(30)}
          color={Theme.PRIMARY}
          name='ios-arrow-back'
        />
      </TouchableOpacity>
      <View style={styles.bodyContainer}>
        <BookingInstructionHeader header={t('headingChooseOnDemandSlot')} />
        {loadingOnDemandSlots && <ActivityIndicator color={Theme.PRIMARY} style={styles.activity} size='small' />}
        {!loadingOnDemandSlots && getEntries()}
      </View>
      <BookingFlowFooter>
        <FormError
          color={Theme.CHARCOAL}
          error={bookingError && { message: bookingError }}
        />
        <View style={styles.footer}>
          <Button
            fontColor={Theme.PRIMARY}
            size={ButtonSize.MEDIUM}
            buttonType={ButtonTypes.OUTLINE}
            title={t('back')}
            onPress={backPressed}
            containerStyle={styles.backContainer}
          />

          <Button
            fontColor={Theme.WHITE}
            size={ButtonSize.MEDIUM}
            isLoading={startOnDemandBookingLoading}
            disabled={!pendingSlot || loadingOnDemandSlots}
            title={nextButtonTitle()}
            onPress={startOnDemandWorkoutPressed}
            containerStyle={styles.nextContainer}
          />
        </View>
      </BookingFlowFooter>
    </View>
  );
};

const mapStateToProps = (state: IAppStore): IMapStateProps => ({
  onDemandSlots: onDemandSlotsSelector(state),
  pendingSlot: state.bookings.bookingWizard.pendingSlot,
  startOnDemandBookingLoading: state.bookings.bookingWizard.startOnDemandBookingLoading,
  bookingError: state.bookings.bookingWizard.makeBookingError,
  gym: currentSelectedGymSelector(state) || undefined,
  loadingOnDemandSlots: state.bookings.bookingWizard.loadingOnDemandSlots,
});

const mapDispatchToProps = (dispatch, props: IParentProps): IMapDispatchProps => ({
  backPressed: () => dispatch(onDemandSlotSelectBackPressAction()),
  onDemandSlotSelected: (startTime, workoutType) => dispatch(onDemandSlotSelectedAction(startTime, workoutType)),
  startOnDemandWorkoutPressed: () => dispatch(startOnDemandWorkoutPressedAction(props.userProfile)),
});

export default connect(mapStateToProps, mapDispatchToProps)(
  translate(['bookings', 'common'], { wait: true })(
    SelectOnDemandStudioTimeSlot,
  ),
);

const styles = StyleSheet.create({
  container: {
    flex: 1,
    justifyContent: 'flex-end',
  },
  bodyContainer: {
    flex: 1,
  },
  footer: {
    flexDirection: 'row',
  },
  backContainer: {
    flex: 0.3,
  },
  nextContainer: {
    flex: 0.7,
    marginLeft: 5,
  },
  optionCard: {
    height: defaultScale(90),
    borderWidth: defaultScale(1),
    borderColor: Theme.GREY,
    borderRadius: defaultScale(3),
    marginVertical: defaultScale(5),
    justifyContent: 'center',
    alignItems: 'center',
  },
  selectedOption: {
    backgroundColor: Theme.DARK_CHARCOAL,
    color: Theme.WHITE,
  },
  slotOptionTitle: {
    ...Theme.textStyle.heading2,
  },
  textSelected: {
    color: Theme.WHITE,
  },
  slotOptionDescription: {
    ...Theme.textStyle.bodyCopyReg,
  },
  activity: {
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center',
  },
});
